import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FieldConfig {
  key: string;
  label: string;
  icon: string;
  required?: boolean;
}

interface BulkEditPreModalProps {
  open: boolean;
  onClose: () => void;
  studentCount: number;
  /** Called with the list of selected field keys when user clicks Continue */
  onConfirm: (selectedFields: string[]) => void;
}

// ─── All editable fields ──────────────────────────────────────────────────────

export const ALL_BULK_FIELDS: FieldConfig[] = [
  { key: 'name_edit',        label: 'Full Name',      icon: 'pi pi-user',          required: true  },
  { key: 'roll_no_edit',     label: 'Roll No',        icon: 'pi pi-hashtag',       required: true  },
  { key: 'section_edit',     label: 'Section',        icon: 'pi pi-th-large',      required: false },
  { key: 'email_id_edit',    label: 'Email',          icon: 'pi pi-envelope',      required: true  },
  { key: 'gender_edit',      label: 'Gender',         icon: 'pi pi-user-edit',     required: true  },
  { key: 'father_name_edit', label: "Father's Name",  icon: 'pi pi-id-card',       required: true  },
  { key: 'father_no_edit',   label: "Father's No",    icon: 'pi pi-phone',         required: true  },
  { key: 'mother_name_edit', label: "Mother's Name",  icon: 'pi pi-id-card',       required: false },
  { key: 'mother_no_edit',   label: "Mother's No",    icon: 'pi pi-phone',         required: false },
  { key: 'blood_group_edit', label: 'Blood Group',    icon: 'pi pi-heart',         required: false },
  { key: 'address_edit',     label: 'Address',        icon: 'pi pi-map-marker',    required: false },
];

// ─── Component ────────────────────────────────────────────────────────────────

const BulkEditPreModal: React.FC<BulkEditPreModalProps> = ({
  open,
  onClose,
  studentCount,
  onConfirm,
}) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isClosing, setIsClosing] = useState(false);

  // Reset selection when modal opens
  useEffect(() => {
    if (open) {
      setSelected(new Set());
    }
  }, [open]);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.premodal-wrapper .p-dialog') as HTMLElement | null;
      const mask   = document.querySelector('.premodal-wrapper')           as HTMLElement | null;
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask)   mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => { onClose(); setIsClosing(false); }, 380);
  };

  const allSelected = selected.size === ALL_BULK_FIELDS.length;

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(ALL_BULK_FIELDS.map(f => f.key)));
    }
  };

  const toggleField = (key: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleConfirm = () => {
    if (selected.size === 0) return;
    onConfirm(Array.from(selected));
    handleClose();
  };

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '4px 0' }}>
      <div style={{
        width: 48, height: 48, borderRadius: 13,
        background: 'linear-gradient(135deg,#1e3a8a,#3b82f6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, boxShadow: '0 6px 18px rgba(59,130,246,.25)',
        border: '2.5px solid white',
      }}>
        <i className="pi pi-sliders-h" style={{ fontSize: 20, color: 'white' }} />
      </div>
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1e40af' }}>
          Choose fields to edit
        </div>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
          {studentCount} student{studentCount !== 1 ? 's' : ''} selected
          &nbsp;·&nbsp; pick which columns appear in the editor
        </div>
      </div>
    </div>
  );

  return (
    <Dialog
      visible={open && !isClosing}
      onHide={handleClose}
      header={headerTemplate}
      style={{ width: '94vw', maxWidth: 560, borderRadius: 22, overflow: 'hidden' }}
      contentStyle={{
        padding: '18px 24px 8px',
        background: 'linear-gradient(180deg,#f8fafc,#f1f5f9)',
      }}
      headerStyle={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '18px 24px',
        borderRadius: '22px 22px 0 0',
      }}
      modal
      draggable={false}
      resizable={false}
      blockScroll
      dismissableMask
      maskClassName="premodal-wrapper"
    >
      <style>{`
        .premodal-wrapper { backdrop-filter:blur(4px); background-color:rgba(0,0,0,.22)!important; animation:pmFadeIn .3s ease; }
        .premodal-wrapper.mask-closing { animation:pmFadeOut .38s ease!important; }
        @keyframes pmFadeIn { from{opacity:0} to{opacity:1} }
        @keyframes pmFadeOut { from{opacity:1} to{opacity:0} }
        .premodal-wrapper .p-dialog { animation:pmSlideIn .3s cubic-bezier(.4,0,.2,1); box-shadow:0 20px 48px -8px rgba(0,0,0,.22)!important; }
        .premodal-wrapper .p-dialog.dialog-closing { animation:pmSlideOut .38s cubic-bezier(.4,0,.2,1)!important; }
        @keyframes pmSlideIn { from{opacity:0;transform:scale(.93) translateY(-16px)} to{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes pmSlideOut { from{opacity:1;transform:scale(1) translateY(0)} to{opacity:0;transform:scale(.9) translateY(18px)} }
        .field-chip { display:flex; align-items:center; gap:9px; padding:9px 13px; border-radius:11px; cursor:pointer; border:1.5px solid #e5e7eb; background:white; transition:all .15s; user-select:none; }
        .field-chip:hover { border-color:#bfdbfe; background:#eff6ff; }
        .field-chip.selected { border-color:#3b82f6; background:#eff6ff; }
        .field-chip.selected .chip-check { background:#3b82f6; border-color:#3b82f6; }
        .field-chip.selected .chip-check::after { opacity:1; }
        .chip-check { width:16px; height:16px; border-radius:5px; border:1.5px solid #d1d5db; background:white; flex-shrink:0; display:flex; align-items:center; justify-content:center; transition:all .12s; }
        .chip-check::after { content:''; display:block; width:8px; height:5px; border-left:2px solid white; border-bottom:2px solid white; transform:rotate(-45deg) translateY(-1px); opacity:0; transition:opacity .1s; }
        .select-all-row { display:flex; align-items:center; justify-content:space-between; padding:10px 14px; border-radius:12px; background:white; border:1.5px solid #e5e7eb; cursor:pointer; margin-bottom:14px; transition:all .15s; }
        .select-all-row:hover { border-color:#bfdbfe; background:#f8faff; }
        .select-all-row.all-on { border-color:#3b82f6; background:#eff6ff; }
        .select-all-row.all-on .chip-check { background:#3b82f6; border-color:#3b82f6; }
        .select-all-row.all-on .chip-check::after { opacity:1; }
      `}</style>

      {/* Select All row */}
      <div
        className={`select-all-row${allSelected ? ' all-on' : ''}`}
        onClick={toggleAll}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="chip-check" />
          <span style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>
            Select all fields
          </span>
        </div>
        <span style={{
          fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
          background: selected.size > 0 ? 'rgba(59,130,246,.12)' : '#f3f4f6',
          color: selected.size > 0 ? '#1e40af' : '#9ca3af',
        }}>
          {selected.size} / {ALL_BULK_FIELDS.length}
        </span>
      </div>

      {/* Field chips grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 20 }}>
        {ALL_BULK_FIELDS.map(field => {
          const isSelected = selected.has(field.key);
          return (
            <div
              key={field.key}
              className={`field-chip${isSelected ? ' selected' : ''}`}
              onClick={() => toggleField(field.key)}
            >
              <div className="chip-check" />
              <i className={field.icon} style={{ fontSize: 13, color: isSelected ? '#3b82f6' : '#9ca3af', flexShrink: 0 }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: isSelected ? '#1e40af' : '#374151', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {field.label}
              </span>
              {field.required && (
                <span style={{ fontSize: 10, color: '#ef4444', flexShrink: 0 }}>*</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Required fields note */}
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 18 }}>
        <span style={{ color: '#ef4444' }}>*</span> Required fields — will always be included when selected
      </div>

      {/* Footer */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 0 6px', borderTop: '1px solid #f0f0f0',
      }}>
        <div style={{ fontSize: 13, color: '#64748b' }}>
          {selected.size === 0
            ? 'No fields selected'
            : <><span style={{ fontWeight: 700, color: '#3b82f6' }}>{selected.size}</span> field{selected.size !== 1 ? 's' : ''} will be editable</>
          }
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button
            label="Cancel"
            icon="pi pi-times"
            className="p-button-outlined"
            style={{ borderRadius: 10, fontSize: 13 }}
            onClick={handleClose}
          />
          <Button
            label={`Continue →`}
            icon="pi pi-arrow-right"
            iconPos="right"
            disabled={selected.size === 0}
            style={{
              borderRadius: 10, fontSize: 13,
              background: 'linear-gradient(135deg,#1e40af,#3b82f6)',
              border: 'none',
              opacity: selected.size === 0 ? 0.45 : 1,
              transition: 'opacity .15s',
            }}
            onClick={handleConfirm}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default BulkEditPreModal;
